import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import { useAuth } from '../hooks/useAuth';
import { track } from '../utils/analytics';

const MAX_SIZE_MB = 5;
const ACCEPTED    = ['image/jpeg', 'image/png', 'image/webp'];

function formatDob(dob) {
  if (!dob) return 'Not set';
  const d = new Date(dob + 'T00:00:00');
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
}

export default function Settings() {
  const { user, profile, refreshProfile } = useAuth();
  const navigate = useNavigate();
  const inputRef = useRef();

  const [displayName, setDisplayName]     = useState(profile?.display_name || '');
  const [nameError, setNameError]         = useState('');
  const [nameSaving, setNameSaving]       = useState(false);
  const [nameSuccess, setNameSuccess]     = useState(false);

  const [photoFile, setPhotoFile]         = useState(null);
  const [photoPreview, setPhotoPreview]   = useState(null);
  const [photoError, setPhotoError]       = useState('');
  const [photoSaving, setPhotoSaving]     = useState(false);
  const [showWarning, setShowWarning]     = useState(false);
  const [dragOver, setDragOver]           = useState(false);

  // ── Username ─────────────────────────────────────────────

  async function handleSaveName(e) {
    e.preventDefault();
    setNameError('');
    setNameSuccess(false);

    if (!displayName.trim())        { setNameError('Display name cannot be empty.'); return; }
    if (displayName.trim().length > 50) { setNameError('Max 50 characters.'); return; }
    if (displayName.trim() === profile?.display_name) { setNameError('That is already your display name.'); return; }

    setNameSaving(true);
    try {
      const { error } = await supabase
        .from('profiles')
        .update({ display_name: displayName.trim() })
        .eq('id', user.id);
      if (error) throw error;
      await refreshProfile();
      setNameSuccess(true);
      track('display_name_changed');
    } catch (err) {
      setNameError(err.message || 'Something went wrong. Try again.');
    } finally {
      setNameSaving(false);
    }
  }

  // ── Photo ────────────────────────────────────────────────

  function handleFile(f) {
    setPhotoError('');
    if (!f) return;
    if (!ACCEPTED.includes(f.type)) { setPhotoError('JPG, PNG, or WEBP only.'); return; }
    if (f.size > MAX_SIZE_MB * 1024 * 1024) { setPhotoError(`Max ${MAX_SIZE_MB}MB.`); return; }
    setPhotoFile(f);
    setPhotoPreview(URL.createObjectURL(f));
  }

  function handleDrop(e) {
    e.preventDefault();
    setDragOver(false);
    handleFile(e.dataTransfer.files[0]);
  }

  async function handlePhotoUpload() {
    if (!photoFile) return;
    setPhotoSaving(true);
    setPhotoError('');
    try {
      const ext  = photoFile.name.split('.').pop();
      const path = `${user.id}/profile.${ext}`;

      const { error: uploadError } = await supabase.storage
        .from('profile-photos')
        .upload(path, photoFile, { upsert: true, contentType: photoFile.type });
      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('profile-photos')
        .getPublicUrl(path);

      // Reset score and ratings, set photo back to pending moderation
      const { error: updateError } = await supabase
        .from('profiles')
        .update({
          profile_photo_url:      publicUrl,
          photo_approved:         false,
          current_score:          null,
          total_ratings_received: 0,
        })
        .eq('id', user.id);
      if (updateError) throw updateError;

      // Delete all existing ratings received so scoring starts fresh
      await supabase
        .from('ratings')
        .delete()
        .eq('rated_user_id', user.id);

      track('photo_replaced');
      await refreshProfile();
      setShowWarning(false);
      setPhotoFile(null);
      setPhotoPreview(null);
      navigate('/profile');
    } catch (err) {
      setPhotoError(err.message || 'Upload failed. Please try again.');
    } finally {
      setPhotoSaving(false);
    }
  }

  return (
    <div className="page" style={{ alignItems: 'center', paddingTop: 100 }}>
      <div className="container">

        <div className="text-center mb-24">
          <h2 style={{ fontFamily: 'var(--font-heading)', color: 'var(--gold)', fontSize: '2.2rem' }}>
            Settings
          </h2>
        </div>

        {/* ── Display name ─────────────────────────────── */}
        <div className="card mb-24">
          <h3 style={{ fontFamily: 'var(--font-heading)', color: 'var(--text)', marginBottom: 6 }}>
            Display name
          </h3>
          <p className="text-muted mb-24" style={{ fontSize: '0.85rem' }}>
            This is how you appear to other users.
          </p>

          <form onSubmit={handleSaveName} noValidate>
            <div className="form-group">
              <label htmlFor="displayName">Display name</label>
              <input
                id="displayName"
                type="text"
                value={displayName}
                onChange={e => { setDisplayName(e.target.value); setNameSuccess(false); setNameError(''); }}
                maxLength={50}
                placeholder="Your display name"
              />
              {nameError   && <p className="form-error">{nameError}</p>}
              {nameSuccess && <p style={{ color: 'var(--valid-color)', fontSize: '0.85rem', marginTop: 4 }}>Display name updated.</p>}
            </div>
            <button className="btn-primary" type="submit" disabled={nameSaving} style={{ width: 'auto', padding: '10px 28px' }}>
              {nameSaving ? 'Saving...' : 'Save name'}
            </button>
          </form>
        </div>

        {/* ── Date of birth ─────────────────────────────── */}
        <div className="card mb-24">
          <h3 style={{ fontFamily: 'var(--font-heading)', color: 'var(--text)', marginBottom: 6 }}>
            Date of birth
          </h3>
          <div style={{
            background: 'rgba(255,255,255,0.04)',
            border: '1px solid var(--border)',
            borderRadius: 'var(--radius)',
            padding: '14px 16px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 12,
          }}>
            <span style={{ color: 'var(--text)', fontSize: '0.95rem' }}>
              {formatDob(profile?.date_of_birth)}
            </span>
            <span style={{
              fontSize: '0.7rem',
              color: 'var(--text-muted)',
              background: 'rgba(255,255,255,0.06)',
              padding: '3px 10px',
              borderRadius: 20,
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
            }}>
              Locked
            </span>
          </div>
          <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)', lineHeight: 1.6 }}>
            Your date of birth cannot be changed after it has been set. It is used to personalize your feed and is never shown publicly.
          </p>
        </div>

        {/* ── Photo ─────────────────────────────────────── */}
        <div className="card mb-24">
          <h3 style={{ fontFamily: 'var(--font-heading)', color: 'var(--text)', marginBottom: 6 }}>
            Profile photo
          </h3>

          {/* Current photo */}
          {profile?.profile_photo_url && (
            <div style={{ marginBottom: 20 }}>
              <img
                src={profile.profile_photo_url}
                alt="Current profile"
                style={{
                  width: '100%',
                  maxHeight: 240,
                  objectFit: 'cover',
                  borderRadius: 'var(--radius)',
                  display: 'block',
                  marginBottom: 8,
                }}
              />
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>Current photo</span>
                <span className={`badge ${profile.photo_approved ? 'badge-live' : 'badge-pending'}`}>
                  {profile.photo_approved ? 'Live' : 'Under review'}
                </span>
              </div>
            </div>
          )}

          {/* Warning about score reset */}
          <div style={{
            background: 'rgba(248, 113, 113, 0.08)',
            border: '1px solid rgba(248, 113, 113, 0.3)',
            borderRadius: 'var(--radius)',
            padding: '12px 16px',
            marginBottom: 20,
            fontSize: '0.82rem',
            color: 'var(--error)',
            lineHeight: 1.7,
          }}>
            Replacing your photo will reset your score and all ratings received to zero. This cannot be undone.
          </div>

          {!showWarning ? (
            <button
              className="btn-ghost"
              onClick={() => setShowWarning(true)}
              style={{ width: '100%', textAlign: 'center' }}
            >
              Replace photo
            </button>
          ) : (
            <>
              {/* Upload zone */}
              <div
                className={`upload-zone ${dragOver ? 'drag-over' : ''}`}
                onClick={() => inputRef.current?.click()}
                onDragOver={e => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={handleDrop}
              >
                <input
                  ref={inputRef}
                  type="file"
                  accept={ACCEPTED.join(',')}
                  onChange={e => handleFile(e.target.files[0])}
                />
                {photoPreview ? (
                  <img src={photoPreview} alt="Your new upload preview" />
                ) : (
                  <div>
                    <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem' }}>
                      Click to select or drag and drop
                    </p>
                    <p style={{ color: 'var(--mid-gray)', fontSize: '0.75rem', marginTop: 6 }}>
                      JPG, PNG, WEBP — max 5MB
                    </p>
                  </div>
                )}
              </div>

              {photoPreview && (
                <button
                  className="btn-ghost mt-8"
                  onClick={() => { setPhotoPreview(null); setPhotoFile(null); }}
                  style={{ width: '100%', textAlign: 'center', fontSize: '0.8rem' }}
                >
                  Choose a different photo
                </button>
              )}

              {photoError && <p className="form-error mt-16">{photoError}</p>}

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 16 }}>
                <button
                  className="btn-ghost"
                  onClick={() => { setShowWarning(false); setPhotoFile(null); setPhotoPreview(null); }}
                  disabled={photoSaving}
                >
                  Cancel
                </button>
                <button
                  className="btn-primary"
                  onClick={handlePhotoUpload}
                  disabled={!photoFile || photoSaving}
                  style={{
                    background: photoFile ? 'var(--error)' : undefined,
                    boxShadow: photoFile ? '0 4px 14px rgba(248,113,113,0.3)' : undefined,
                  }}
                >
                  {photoSaving ? 'Uploading...' : 'Replace and reset score'}
                </button>
              </div>
            </>
          )}
        </div>

        <button className="btn-ghost" onClick={() => navigate('/profile')} style={{ width: '100%', textAlign: 'center' }}>
          Back to profile
        </button>

      </div>
    </div>
  );
}
